import { makeStyles } from '@material-ui/core'
import React, { useState } from 'react'
import { sanitizeFetchType, useMutation } from 'react-admin'
import { ConfirmDialog } from '../../../common/ConfirmDialog'
import { ResourceName } from '../../../../core/ResourceName'
import { DoorStatuses } from '../../../../core/enum/DoorStatuses'
import Button from '../../../common/customized-mui-components/Button'
import { Authority } from '../../../../core/auth/Authority'
import { useHasAuthority } from '../../../hooks/useHasAuthority'
import {
  DepositorDoorStatusDialog,
  DepositorLockerStatusDialog,
} from '../DepositorDialogs'
import { DepositorDto } from '../../../../core/dto/device/depositor/depositor.dto'
import { LockerStatuses } from '../../../../core/enum/LockerStatuses'

const useStyles = makeStyles({
  button: {
    margin: '2px',
  },
})

export const DepositorActionButton = ({
  depositorAction,
  ...props
}: {
  record: DepositorDto
  depositorAction: 'openLocker' | 'releaseLocker'
}) => {
  const { record: depositor } = props
  const hasAuthority = useHasAuthority()
  const classes = useStyles()
  const [mutate, { loading: mutationLoading }] = useMutation()
  const [confirmOpen, setConfirmOpen] = useState<boolean>(false)
  const [statusDialogOpen, setStatusDialogOpen] = useState<boolean>(false)
  const [doorStatus, setDoorStatus] = useState<DoorStatuses | undefined>(
    undefined,
  )
  const [lockerStatus, setLockerStatus] = useState<LockerStatuses | undefined>(
    undefined,
  )

  const handleOpenLocker = async () => {
    setConfirmOpen(false)
    setStatusDialogOpen(true)
    const { data: status } = await mutate(
      {
        type: sanitizeFetchType(depositorAction),
        resource: ResourceName.DEPOSITORS,
        payload: {
          id: depositor?.id,
        },
      },
      {
        returnPromise: true,
      },
    )
    if (depositorAction === 'openLocker') setDoorStatus(status?.door_status)
    else setLockerStatus(status?.locker_status)
  }

  const handleCloseStatusDialog = () => {
    setStatusDialogOpen(false)
    setDoorStatus(undefined)
  }

  return (
    <>
      <ConfirmDialog
        open={confirmOpen}
        cancel={() => setConfirmOpen(false)}
        confirm={handleOpenLocker}
        source={depositorAction}
        action="set"
        resource={ResourceName.DEPOSITORS}
      />
      {depositorAction === 'openLocker' ? (
        <DepositorDoorStatusDialog
          doorStatus={doorStatus}
          handleCloseStatusDialog={handleCloseStatusDialog}
          statusDialogOpen={statusDialogOpen}
        />
      ) : (
        <DepositorLockerStatusDialog
          lockerStatus={lockerStatus}
          handleCloseStatusDialog={handleCloseStatusDialog}
          statusDialogOpen={statusDialogOpen}
        />
      )}
      <Button
        className={classes.button}
        variant="contained"
        color="primary"
        label={`resources.depositors.actions.${depositorAction}`}
        useSmallVersionBreakpoint={false}
        disabled={
          (depositorAction === 'openLocker'
            ? !hasAuthority(Authority.DEPOSITORS_OPEN_LOCKER_BUTTON)
            : !hasAuthority(Authority.DEPOSITORS_RELEASE_LOCKER_BUTTON)) ||
          mutationLoading
        }
        onClick={() => setConfirmOpen(true)}
      />
    </>
  )
}
